import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { helpContentVersion, helpLangReady, loadHelpLang, subscribeHelpContent } from "@/content/help";
import { LANG_PARAM, langFromSearch, type SeoLang } from "@/lib/seo/site";

/** The query parameters a help page passes on to the help pages it links to: the page language (?lang=) and the
 *  workspace context (?ws=, which decides the Photohawk app card: src/content/help/promo.ts). */
const KEPT = [LANG_PARAM, "ws"] as const;

export const keptParams = (search: string): string => {
  const from = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const k of KEPT) {
    const v = from.get(k);
    if (v) out.set(k, v);
  }
  const s = out.toString();
  return s ? `?${s}` : "";
};

/** A help path with this page's ?lang= and ?ws= kept. */
export const useHelpTo = () => {
  const { search } = useLocation();
  const q = keptParams(search);
  return (path: string) => `${path}${q}`;
};

/** The page language the URL asks for, if the help content has it ("en" otherwise). */
export const useHelpLang = (langs: readonly SeoLang[]): SeoLang => {
  const { search } = useLocation();
  return langFromSearch(search, langs) ?? "en";
};

/** "29 October 2026" in a language (UTC, so the server and the browser agree). */
export const formatHelpDate = (iso: string, lang: SeoLang): string => {
  try {
    return new Intl.DateTimeFormat(lang === "no" ? "nb" : lang, { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
  } catch {
    return iso;
  }
};

/** Re-renders when a help language finishes loading in the browser. */
export const useHelpContentVersion = (): number => useSyncExternalStore(subscribeHelpContent, helpContentVersion, helpContentVersion);

/** Loads `lang`'s articles if the browser doesn't have them yet; true once they're in (always, on the server). Until
 *  then the help pages show English, which is what the content functions fall back to. */
export const useHelpContent = (lang: SeoLang): boolean => {
  useHelpContentVersion();
  const ready = helpLangReady(lang);
  useEffect(() => {
    if (!ready) void loadHelpLang(lang).catch(() => undefined);
  }, [lang, ready]);
  return ready;
};
